import { Injectable } from '@angular/core';
import { ICognitoAuth } from '@core/models';
import { generatePkcePair, generateState } from '@core/utils';
import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  ICognitoUserAttributeData,
  ISignUpResult
} from 'amazon-cognito-identity-js';

type AuthConfig = { Auth: ICognitoAuth };
type AuthEventListener = (event: { event: string; data: any; message?: string }) => void;
type FederatedSignInOptions = { customProvider?: string; nonce?: string };

const OAUTH_STATE_KEY = 'myhivve-oauth-state';
const OAUTH_PKCE_VERIFIER_KEY = 'myhivve-oauth-pkce-verifier';
const OAUTH_NONCE_KEY = 'myhivve-oauth-nonce';
const OAUTH_CLIENT_ID_KEY = 'myhivve-oauth-client-id';
const OAUTH_REDIRECT_URI_KEY = 'myhivve-oauth-redirect-uri';
const OAUTH_IDENTITY_PROVIDER_KEY = 'myhivve-oauth-identity-provider';
const LEGACY_AMPLIFY_STATE_KEY = 'oauth_state';
const LEGACY_AMPLIFY_PKCE_VERIFIER_KEY = 'ouath_pkce_key';

interface SignUpParams {
  username: string;
  password: string;
  attributes?: Record<string, string | boolean | null | undefined>;
  clientMetadata?: Record<string, string>;
}

export interface OAuthCallbackContext {
  clientId: string;
  code: string;
  codeVerifier: string;
  redirectUri: string;
  expectedNonce?: string | null;
  identityProvider?: string | null;
}

@Injectable({ providedIn: 'root' })
export class CognitoAuthClientService {
  private userPool?: CognitoUserPool;
  private authConfig?: ICognitoAuth;
  private listeners: AuthEventListener[] = [];

  configure(config: AuthConfig): void {
    this.authConfig = config.Auth;
    this.userPool = this.createUserPool(this.authConfig);
  }

  configureAuth(config: AuthConfig): void {
    this.configure(config);
  }

  listen(listener: AuthEventListener): void {
    this.listeners.push(listener);
  }

  currentAuthenticatedUser<T = any>(): Promise<T> {
    const user = this.getUserPool().getCurrentUser();

    if (!user) {
      return Promise.reject(this.createNoCurrentUserError());
    }

    return new Promise<T>((resolve, reject) => {
      user.getSession((error: Error | null, session: CognitoUserSession | null) => {
        if (error || !session?.isValid()) {
          reject(error ?? this.createNoCurrentUserError());
          return;
        }

        void this.hydrateUser(user, session).then(
          hydratedUser => resolve(hydratedUser as T),
          reject
        );
      });
    });
  }

  updateUserAttributes(
    user: CognitoUser,
    attributes: Record<string, string | boolean | null | undefined>
  ): Promise<string | void> {
    const userAttributes = this.toCognitoAttributes(attributes);

    return new Promise<string | void>((resolve, reject) => {
      user.updateAttributes(userAttributes, (error, result) => {
        if (error) {
          reject(error);
          return;
        }

        resolve(result ?? 'SUCCESS');
      });
    });
  }

  signOut(): Promise<void> {
    const user = this.getUserPool().getCurrentUser();
    user?.signOut();
    this.emit('signOut', null);

    return Promise.resolve();
  }

  signUp(params: SignUpParams): Promise<ISignUpResult> {
    return new Promise((resolve, reject) => {
      this.getUserPool().signUp(
        params.username,
        params.password,
        this.toCognitoAttributes(params.attributes ?? {}),
        [],
        (error, result) => {
          if (error || !result) {
            reject(error);
            return;
          }

          resolve(result);
        },
        params.clientMetadata
      );
    });
  }

  confirmSignUp(username: string, code: string): Promise<unknown> {
    const user = this.createUser(username);

    return new Promise((resolve, reject) => {
      user.confirmRegistration(code, false, (error, result) => {
        if (error) {
          reject(error);
          return;
        }

        resolve(result);
      });
    });
  }

  resendSignUp(username: string, clientMetadata?: Record<string, string>): Promise<unknown> {
    const user = this.createUser(username);

    return new Promise((resolve, reject) => {
      user.resendConfirmationCode((error, result) => {
        if (error) {
          reject(error);
          return;
        }

        resolve(result);
      }, clientMetadata);
    });
  }

  async createFederatedSignInUrl(options: FederatedSignInOptions = {}): Promise<string> {
    const config = this.getAuthConfig();
    const pkce = await generatePkcePair();
    const state = generateState(32);
    const nonce = options.nonce ?? generateState(32);
    const responseType = config.oauth.responseType ?? 'code';

    if (responseType !== 'code') {
      throw this.createOAuthError(
        'unsupported_response_type',
        'Only authorization code flow is supported.'
      );
    }

    sessionStorage.setItem(OAUTH_STATE_KEY, state);
    sessionStorage.setItem(OAUTH_PKCE_VERIFIER_KEY, pkce.code_verifier);
    sessionStorage.setItem(OAUTH_NONCE_KEY, nonce);
    sessionStorage.setItem(OAUTH_CLIENT_ID_KEY, config.userPoolWebClientId);
    sessionStorage.setItem(OAUTH_REDIRECT_URI_KEY, config.oauth.redirectSignIn);
    if (options.customProvider) {
      sessionStorage.setItem(OAUTH_IDENTITY_PROVIDER_KEY, options.customProvider);
    } else {
      sessionStorage.removeItem(OAUTH_IDENTITY_PROVIDER_KEY);
    }
    sessionStorage.removeItem(LEGACY_AMPLIFY_STATE_KEY);
    sessionStorage.removeItem(LEGACY_AMPLIFY_PKCE_VERIFIER_KEY);

    const params = new URLSearchParams({
      redirect_uri: config.oauth.redirectSignIn,
      response_type: responseType,
      client_id: config.userPoolWebClientId,
      state,
      nonce,
      code_challenge: pkce.code_challenge,
      code_challenge_method: pkce.code_challenge_method
    });

    if (options.customProvider) {
      params.set('identity_provider', options.customProvider);
    }

    if (config.oauth.scope?.length) {
      params.set('scope', config.oauth.scope.join(' '));
    }

    return `https://${this.getOAuthDomain(config)}/oauth2/authorize?${params.toString()}`;
  }

  async handleOAuthCallback(currentUrl: string): Promise<OAuthCallbackContext> {
    const url = new URL(currentUrl);
    const error = url.searchParams.get('error');

    if (error) {
      throw this.createOAuthError(error, url.searchParams.get('error_description'));
    }

    const code = url.searchParams.get('code');
    if (!code) {
      throw this.createOAuthError(
        'invalid_request',
        'OAuth callback did not include an authorization code.'
      );
    }

    const expectedState = this.consumeStoredValue(OAUTH_STATE_KEY, LEGACY_AMPLIFY_STATE_KEY);
    const actualState = url.searchParams.get('state');

    if (!expectedState || !actualState || expectedState !== actualState) {
      this.clearOAuthState();
      throw this.createOAuthError(
        'invalid_state',
        'The sign-in response could not be verified. Please try again.'
      );
    }

    const codeVerifier = this.consumeStoredValue(
      OAUTH_PKCE_VERIFIER_KEY,
      LEGACY_AMPLIFY_PKCE_VERIFIER_KEY
    );

    if (!codeVerifier) {
      this.clearOAuthState();
      throw this.createOAuthError(
        'invalid_request',
        'The sign-in request expired. Please try again.'
      );
    }

    return {
      clientId:
        this.consumeStoredValue(OAUTH_CLIENT_ID_KEY) ?? this.getAuthConfig().userPoolWebClientId,
      code,
      codeVerifier,
      redirectUri:
        this.consumeStoredValue(OAUTH_REDIRECT_URI_KEY) ??
        this.getAuthConfig().oauth.redirectSignIn,
      expectedNonce: this.consumeStoredValue(OAUTH_NONCE_KEY),
      identityProvider: this.consumeStoredValue(OAUTH_IDENTITY_PROVIDER_KEY)
    };
  }

  peekPendingIdentityProvider(): string | null {
    return sessionStorage.getItem(OAUTH_IDENTITY_PROVIDER_KEY);
  }

  clearOAuthState(): void {
    sessionStorage.removeItem(OAUTH_STATE_KEY);
    sessionStorage.removeItem(OAUTH_PKCE_VERIFIER_KEY);
    sessionStorage.removeItem(OAUTH_NONCE_KEY);
    sessionStorage.removeItem(OAUTH_CLIENT_ID_KEY);
    sessionStorage.removeItem(OAUTH_REDIRECT_URI_KEY);
    sessionStorage.removeItem(OAUTH_IDENTITY_PROVIDER_KEY);
    sessionStorage.removeItem(LEGACY_AMPLIFY_STATE_KEY);
    sessionStorage.removeItem(LEGACY_AMPLIFY_PKCE_VERIFIER_KEY);
  }

  private createUser(username: string): CognitoUser {
    const user = new CognitoUser({
      Username: username,
      Pool: this.getUserPool()
    });

    if (this.authConfig?.authenticationFlowType) {
      user.setAuthenticationFlowType(this.authConfig.authenticationFlowType);
    }

    return user;
  }

  private createUserPool(config: ICognitoAuth): CognitoUserPool {
    return new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.userPoolWebClientId
    });
  }

  private getUserPool(): CognitoUserPool {
    if (!this.userPool) {
      throw new Error('Cognito auth client has not been configured.');
    }

    return this.userPool;
  }

  private getAuthConfig(): ICognitoAuth {
    if (!this.authConfig) {
      throw new Error('Cognito auth client has not been configured.');
    }

    return this.authConfig;
  }

  private getOAuthDomain(config: ICognitoAuth): string {
    return config.oauth.customDomain || config.oauth.domain;
  }

  private consumeStoredValue(primaryKey: string, legacyKey?: string): string | null {
    const value =
      sessionStorage.getItem(primaryKey) ?? (legacyKey ? sessionStorage.getItem(legacyKey) : null);
    sessionStorage.removeItem(primaryKey);
    if (legacyKey && legacyKey !== primaryKey) {
      sessionStorage.removeItem(legacyKey);
    }

    return value;
  }

  private hydrateUser(user: CognitoUser, session?: CognitoUserSession): Promise<CognitoUser> {
    if (session) {
      user.setSignInUserSession(session);
    }

    this.applySessionShape(user);

    return new Promise(resolve => {
      user.getUserAttributes((error, attributes) => {
        if (!error && attributes) {
          (user as any).attributes = {
            ...(user as any).attributes,
            ...this.toAttributeMap(attributes)
          };
        }

        resolve(user);
      });
    });
  }

  private applySessionShape(user: CognitoUser): CognitoUser {
    const session = user.getSignInUserSession();
    const idTokenPayload = session?.getIdToken()?.payload ?? {};

    (user as any).username = user.getUsername();
    (user as any).authenticationFlowType = user.getAuthenticationFlowType?.();
    (user as any).signInUserSession = session;
    (user as any).attributes = {
      ...idTokenPayload,
      username: user.getUsername()
    };

    return user;
  }

  private toCognitoAttributes(
    attributes: Record<string, string | boolean | null | undefined>
  ): CognitoUserAttribute[] {
    return Object.entries(attributes)
      .filter(([, value]) => value !== undefined && value !== null)
      .map(
        ([Name, Value]) =>
          new CognitoUserAttribute({
            Name,
            Value: String(Value)
          })
      );
  }

  private toAttributeMap(attributes: ICognitoUserAttributeData[]): Record<string, string> {
    return attributes.reduce<Record<string, string>>((acc, attr) => {
      acc[attr.Name] = attr.Value;
      return acc;
    }, {});
  }

  private emit(event: string, data: any, message?: string): void {
    this.listeners.forEach(listener => listener({ event, data, message }));
  }

  private createNoCurrentUserError(): Error {
    const error = new Error('No current authenticated Cognito user');
    (error as any).code = 'NoCurrentUser';

    return error;
  }

  private createOAuthError(code: string, description?: string | null): Error {
    const error = new Error(description || code);
    (error as any).code = code;

    return error;
  }
}
